// ** React Imports
import { Suspense, lazy, useEffect } from "react"
import { Ability, AbilityBuilder } from "@casl/ability"
import { useAbility } from "@casl/react"
import { useReactOidc, OidcSecure } from "@axa-fr/react-oidc-context"

// ** Utils
import { useLayout } from "@hooks/useLayout"
import { AbilityContext } from "@src/utility/context/Can"
import { useRouterTransition } from "@hooks/useRouterTransition"

// ** Custom Components
// import Spinner from '@components/spinner/Loading-spinner' // Uncomment if your require content fallback
import LayoutWrapper from "@layouts/components/layout-wrapper"

// ** Router Components
import {
  BrowserRouter as AppRouter,
  Route,
  Switch,
  Redirect
} from "react-router-dom"

// ** Routes & Default Routes
import { DefaultRoute, Routes } from "./routes"

// ** Layouts
import BlankLayout from "@layouts/BlankLayout"
import VerticalLayout from "@src/layouts/VerticalLayout"
import HorizontalLayout from "@src/layouts/HorizontalLayout"
import {
  ACL_MANAGE,
  ACL_READ,
  ACL_SUBJECT_ORGANIZATION,
  ACL_SUBJECT_PROFILE,
  ACL_SUBJECT_SERVICE,
  ACL_SUBJECT_INSTRUCTION
} from "@configs/acl/constants"

import AddCertificateHSMOrg from "@views/iframes/AddCertificateHSMOrg"
import AddCertificateHSMOrgEn from "@views/iframes/AddCertificateHSMOrgEn"
import Logout from "@views/logout/Logout"

import AddCertificateTokenOrg from "@views/iframes/AddCertificateTokenOrg"
import AddCertificateTokenOrgEn from "@views/iframes/AddCertificateTokenOrgEn"

import AddCertificateHSMPer from "@views/iframes/AddCertificateHSMPer"
import AddCertificateHSMPerEn from "@views/iframes/AddCertificateHSMPerEn"

import AddCertificateTokenPer from "@views/iframes/AddCertificateTokenPer"
import AddCertificateTokenPerEn from "@views/iframes/AddCertificateTokenPerEn"

import MemberIframe from "@views/iframes/MemberIframe"

import InviteMember from "@views/iframes/InviteMember"

import ActiveAccount from "@views/activeAccount/ActiveAccount"
import ChangePassword from "@views/iframes/ChangePassword"
import ChangePasswordEn from "@views/iframes/ChangePasswordEn"
import VerifyEmailPhone from "@views/verifyEmailPhone/VerifyEmailPhone"
import VerifyPhone from "@views/iframes/VerifyPhone"
import VerifyPhoneEn from "@views/iframes/VerifyPhoneEn"
import ChangeEmail from "@views/iframes/ChangeEmail"
import ChangeEmailEn from "@views/iframes/ChangeEmailEn"
import ChangePhone from "@views/iframes/ChangePhone"
import ChangePhoneEn from "@views/iframes/ChangePhoneEn"
import ChangeAccountInfoIframe from "../views/iframes/ChangeAccountInfoIframe"

const Router = () => {
  // ** Hooks
  const [layout, setLayout] = useLayout()
  const [transition, setTransition] = useRouterTransition()
  const { oidcUser, login } = useReactOidc()

  // ** ACL Ability Context
  const ability = useAbility(AbilityContext)

  useEffect(() => {
    const { can, rules } = new AbilityBuilder(Ability)

    can(ACL_MANAGE, ACL_SUBJECT_PROFILE)
    can(ACL_MANAGE, ACL_SUBJECT_ORGANIZATION)
    can(ACL_MANAGE, ACL_SUBJECT_SERVICE)
    can(ACL_READ, ACL_SUBJECT_INSTRUCTION)

    ability.update(rules)
  }, [oidcUser])

  // ** Default Layout
  const DefaultLayout =
    layout === "horizontal" ? "HorizontalLayout" : "VerticalLayout"

  // ** All of the available layouts
  const Layouts = { BlankLayout, VerticalLayout, HorizontalLayout }

  // ** Current Active Item
  const currentActiveItem = null

  // ** Return Filtered Array of Routes & Paths
  const LayoutRoutesAndPaths = (layout) => {
    const LayoutRoutes = []
    const LayoutPaths = []

    if (Routes) {
      Routes.filter((route) => {
        // ** Checks if Route layout or Default layout matches current layout
        if (
          route.layout === layout ||
          (route.layout === undefined && DefaultLayout === layout)
        ) {
          LayoutRoutes.push(route)
          LayoutPaths.push(route.path)
        }
      })
    }

    return { LayoutRoutes, LayoutPaths }
  }

  const NotAuthorized = lazy(() => import("@src/views/other/NotAuthorized"))

  // ** Init Error Component
  const Error = lazy(() => import("@src/views/other/Error"))

  /**
   ** Final Route Component Checks for Login & User Role and then redirects to the route
   */
  const FinalRoute = (props) => {
    const route = props.route
    let action, resource

    // ** Assign vars based on route meta
    if (route.meta) {
      action = route.meta.action ? route.meta.action : null
      resource = route.meta.resource ? route.meta.resource : null
    }

    if (
      (!oidcUser && route.meta === undefined) ||
      (!oidcUser &&
        route.meta &&
        !route.meta.authRoute &&
        !route.meta.publicRoute)
    ) {
      /**
       ** If user is not Logged in & route meta is undefined
       ** OR
       ** If user is not Logged in & route.meta.authRoute, !route.meta.publicRoute are undefined
       ** Then redirect user to login
       */
      return (
        <OidcSecure>
          <route.component {...props} />
        </OidcSecure>
      )
    } else if (route.meta && route.meta.authRoute && oidcUser) {
      // ** If route has meta and authRole and user is Logged in then redirect user to home page (DefaultRoute)
      return <Redirect to="/" />
    } else if (oidcUser && !ability.can(action || ACL_READ, resource)) {
      // ** If user is Logged in and doesn't have ability to visit the page redirect the user to Not Authorized
      return <Redirect to="/not-authorized" />
    } else {
      // ** If none of the above render component
      return <route.component {...props} />
    }
  }

  // ** Return Route to Render
  const ResolveRoutes = () => {
    return Object.keys(Layouts).map((layout, index) => {
      // ** Convert Layout parameter to Layout Component
      // ? Note: make sure to keep layout and component name equal

      const LayoutTag = Layouts[layout]

      // ** Get Routes and Paths of the Layout
      const { LayoutRoutes, LayoutPaths } = LayoutRoutesAndPaths(layout)

      // ** We have freedom to display different layout for different route
      // ** We have made LayoutTag dynamic based on layout, we can also replace it with the only layout component,
      // ** that we want to implement like VerticalLayout or HorizontalLayout
      // ** We segregated all the routes based on the layouts and Resolved all those routes inside layouts

      // ** RouterProps to pass them to Layouts
      const routerProps = {}

      return (
        <Route path={LayoutPaths} key={index}>
          <LayoutTag
            routerProps={routerProps}
            layout={layout}
            setLayout={setLayout}
            transition={transition}
            setTransition={setTransition}
            currentActiveItem={currentActiveItem}
          >
            <Switch>
              {LayoutRoutes.map((route) => {
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    exact={route.exact === true}
                    render={(props) => {
                      // ** Assign props to routerProps
                      Object.assign(routerProps, {
                        ...props,
                        meta: route.meta
                      })

                      return (
                        <Suspense fallback={null}>
                          {/* Layout Wrapper to add classes based on route's layout, appLayout and className */}
                          <LayoutWrapper
                            layout={DefaultLayout}
                            transition={transition}
                            setTransition={setTransition}
                            /* Conditional props */
                            /*eslint-disable */
                            {...(route.appLayout
                              ? {
                                appLayout: route.appLayout,
                              }
                              : {})}
                            {...(route.meta
                              ? {
                                routeMeta: route.meta,
                              }
                              : {})}
                            {...(route.className
                              ? {
                                wrapperClass: route.className,
                              }
                              : {})}
                          /*eslint-enable */
                          >
                            <FinalRoute route={route} {...props} />
                          </LayoutWrapper>
                        </Suspense>
                      )
                    }}
                  />
                )
              })}
            </Switch>
          </LayoutTag>
        </Route>
      )
    })
  }

  return (
    <AppRouter basename={process.env.REACT_APP_BASENAME}>
      <Switch>
        {/* If user is logged in Redirect user to DefaultRoute else to login */}
        <Route exact path="/" render={() => {

          return <Redirect to={DefaultRoute} />
        }} />

        <Route
          exact
          path="/active-account/:id"
          render={(props) => (
            <Layouts.BlankLayout>
              <ActiveAccount />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/verify/:id"
          render={(props) => (
            <Layouts.BlankLayout>
              <VerifyEmailPhone />
            </Layouts.BlankLayout>
          )}
        />

        {/* Iframe for all app */}
        <Route
          exact
          path="/profile/:id/add-certificate-hsm/:appId/:messageId/vi"
          render={(props) => (
            <Layouts.BlankLayout>
              <AddCertificateHSMPer />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/:id/add-certificate-hsm/:appId/:messageId/en"
          render={(props) => (
            <Layouts.BlankLayout>
              <AddCertificateHSMPerEn />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/:id/add-certificate-token/:appId/:messageId/vi"
          render={(props) => (
            <Layouts.BlankLayout>
              <AddCertificateTokenPer />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/:id/add-certificate-token/:appId/:messageId/en"
          render={(props) => (
            <Layouts.BlankLayout>
              <AddCertificateTokenPerEn />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/change-password/vi"
          render={(props) => (
            <Layouts.BlankLayout>
              <ChangePassword />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/change-password/en"
          render={(props) => (
            <Layouts.BlankLayout>
              <ChangePasswordEn />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/organizations/:id/add-certificate-hsm/:appId/:messageId/vi"
          render={(props) => (
            <Layouts.BlankLayout>
              <AddCertificateHSMOrg />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/organizations/:id/add-certificate-hsm/:appId/:messageId/en"
          render={(props) => (
            <Layouts.BlankLayout>
              <AddCertificateHSMOrgEn />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/organizations/:id/add-certificate-token/:appId/:messageId/vi"
          render={(props) => (
            <Layouts.BlankLayout>
              <AddCertificateTokenOrg />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/organizations/:id/add-certificate-token/:appId/:messageId/en"
          render={(props) => (
            <Layouts.BlankLayout>
              <AddCertificateTokenOrgEn />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/organizations/:id/add-member/:appId/:messageId/:lang"
          render={(props) => (
            <Layouts.BlankLayout>
              <MemberIframe />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/organizations/:id/invite-member/:appId/:messageId/:lang"
          render={(props) => (
            <Layouts.BlankLayout>
              <InviteMember />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/verify-phone/vi"
          render={(props) => (
            <Layouts.BlankLayout>
              <VerifyPhone />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/verify-phone/en"
          render={(props) => (
            <Layouts.BlankLayout>
              <VerifyPhoneEn />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/change-email/:email/vi"
          render={(props) => (
            <Layouts.BlankLayout>
              <ChangeEmail />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/change-email/:email/en"
          render={(props) => (
            <Layouts.BlankLayout>
              <ChangeEmailEn />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/change-phone/:phone/vi"
          render={(props) => (
            <Layouts.BlankLayout>
              <ChangePhone />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/change-phone/:phone/en"
          render={(props) => (
            <Layouts.BlankLayout>
              <ChangePhoneEn />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/profile/change-account-info/:appId"
          render={(props) => (
            <Layouts.BlankLayout>
              <ChangeAccountInfoIframe />
            </Layouts.BlankLayout>
          )}
        />

        {/* Not Auth Route */}
        <Route
          exact
          path="/not-authorized"
          render={(props) => (
            <Layouts.BlankLayout>
              <NotAuthorized />
            </Layouts.BlankLayout>
          )}
        />

        <Route
          exact
          path="/logout"
          render={(props) => (
            <Layouts.BlankLayout>
              <Logout />
            </Layouts.BlankLayout>
          )}
        />
        {ResolveRoutes()}

        {/* NotFound Error page */}
        <Route path="*" component={Error} />
      </Switch>
    </AppRouter>
  )
}

export default Router
